/**
 * Resource detail data hooks. Reads from the backend in normal use, falls
 * back to seeded demo data when `?demo=1` (or VITE_DEMO_MODE) is set.
 *
 * The backend endpoint is forthcoming (`hub#10`); until then the demo path
 * is what stakeholders see in design reviews.
 */

import { useQuery } from '@tanstack/react-query';

import { hubFetch } from '@/api/client';
import { useEnvStore } from '@/env/envStore';
import { demoResourceDetail, isDemoModeRequested } from '@/lib/demoData';
import type { ResourceDetail, ResourceTimeRange } from '@/models/resourceDetail';

interface UseResourceDetailArgs {
  resourceId: string | null;
  range: ResourceTimeRange;
}

export function useResourceDetail({ resourceId, range }: UseResourceDetailArgs) {
  const env = useEnvStore((s) => s.env);
  const demo = isDemoModeRequested();
  return useQuery({
    enabled: resourceId !== null,
    queryKey: ['resourceDetail', env, resourceId, range, demo],
    queryFn: async (): Promise<ResourceDetail> => {
      if (resourceId === null) {
        throw new Error('resourceId is required');
      }
      if (demo) {
        return demoResourceDetail(resourceId, range);
      }
      return hubFetch<ResourceDetail>(`/api/resources/${encodeURIComponent(resourceId)}`, {
        searchParams: { range },
      });
    },
    staleTime: 15_000,
  });
}
